import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { companyApi, ApiError, uploadFileToSignedUrl } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, TextArea } from '../components/Field.tsx';
import { FileDrop } from '../components/FileDrop.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import type { CorrectiveAction, CorrectiveActionStatus } from '../lib/types.ts';

const STATUS_META: Record<CorrectiveActionStatus, { label: string; cls: string }> = {
  open: { label: 'Action needed', cls: 'bg-amber-50 text-amber-700' },
  responded: { label: 'Submitted', cls: 'bg-blue-50 text-blue-700' },
  approved: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-700' },
  rejected: { label: 'Sent back', cls: 'bg-red-50 text-red-700' },
};

function assetLabel(ca: CorrectiveAction): string {
  const v = ca.asset_audit?.asset;
  return [v?.year, v?.make, v?.model].filter(Boolean).join(' ') || v?.registration_plate || 'asset';
}

export function CompanyCorrectiveActions() {
  const { data, isLoading } = useQuery({
    queryKey: ['me', 'corrective-actions'],
    queryFn: () => companyApi.get<{ corrective_actions: CorrectiveAction[] }>('/api/me/corrective-actions'),
  });
  const [responding, setResponding] = useState<CorrectiveAction | null>(null);

  if (isLoading) return <CenteredSpinner />;
  const items = data?.corrective_actions ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Corrective actions</h1>
        <p className="mt-1 text-sm text-slate-500">
          Items raised from asset audits that need your attention. Respond with what you&apos;ve
          done and an optional photo.
        </p>
      </div>

      {items.length === 0 ? (
        <EmptyState title="Nothing outstanding" description="You have no corrective actions right now." />
      ) : (
        <div className="space-y-3">
          {items.map((ca) => {
            const meta = STATUS_META[ca.status];
            const canRespond = ca.status === 'open' || ca.status === 'rejected';
            return (
              <div key={ca.id} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{ca.question_label}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs ${meta.cls}`}>{meta.label}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">{assetLabel(ca)}</div>
                    {ca.description && <div className="mt-1 text-sm text-slate-700">{ca.description}</div>}
                  </div>
                  {canRespond && (
                    <Btn size="sm" onClick={() => setResponding(ca)}>
                      {ca.status === 'rejected' ? 'Respond again' : 'Respond'}
                    </Btn>
                  )}
                </div>
                {ca.company_comment && (
                  <div className="mt-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    Your response: {ca.company_comment}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {responding && <RespondModal action={responding} onClose={() => setResponding(null)} />}
    </div>
  );
}

function RespondModal({ action, onClose }: { action: CorrectiveAction; onClose: () => void }) {
  const qc = useQueryClient();
  const [comment, setComment] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [step, setStep] = useState<'idle' | 'busy'>('idle');
  const [error, setError] = useState<unknown>(null);

  async function submit() {
    setError(null);
    setStep('busy');
    try {
      let photoPath: string | null = null;
      if (file) {
        const presigned = await companyApi.post<{ path: string; url: string }>(
          `/api/me/corrective-actions/${action.id}/photo-upload-url`,
          { file_name: file.name, mime_type: file.type || 'image/jpeg' },
        );
        await uploadFileToSignedUrl(presigned.url, file);
        photoPath = presigned.path;
      }
      await companyApi.post(`/api/me/corrective-actions/${action.id}/respond`, {
        comment: comment.trim(),
        photo_path: photoPath,
      });
      await qc.invalidateQueries({ queryKey: ['me', 'corrective-actions'] });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err : err);
    } finally {
      setStep('idle');
    }
  }

  return (
    <Modal
      open
      onClose={() => step === 'idle' && onClose()}
      title="Respond to corrective action"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose} disabled={step !== 'idle'}>
            Cancel
          </Btn>
          <Btn onClick={submit} loading={step !== 'idle'} disabled={!comment.trim()}>
            Submit response
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <div className="font-medium">{action.question_label}</div>
          {action.description && <div className="mt-0.5 text-xs">{action.description}</div>}
        </div>
        <Field label="What did you do?" required>
          <TextArea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
        <Field label="Photo (optional)">
          <FileDrop multiple={false} accept="image/*" onFiles={(f) => setFile(f.item(0))} hint="JPEG or PNG" />
          {file && <div className="mt-2 text-sm text-slate-600">{file.name}</div>}
        </Field>
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
